(() => {
  const SELECTORS = {
    section: '[data-floating-video]',
    media: '[data-floating-video-media]',
    mediaItem: '.floating-video__media-item',
    shopifyVideo: '[data-floating-video-shopify]',
    youtubeVideo: '[data-floating-video-youtube]',
  };

  const CLASSES = {
    desktopMedia: 'floating-video__media-item--desktop',
    mobileMedia: 'floating-video__media-item--mobile',
  };

  const MOBILE_BREAKPOINT = 989;
  const OBSERVER_THRESHOLD = 0.25;

  class FloatingVideo {
    constructor(section) {
      this.section = section;
      this.media = section.querySelector(SELECTORS.media);
      this.autoplay = section.dataset.autoplay === 'true';
      this.isIntersecting = false;

      this.handleIntersection = this.handleIntersection.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleYouTubeLoad = this.handleYouTubeLoad.bind(this);

      this.init();
    }

    init() {
      if (!this.media || this.section.dataset.floatingVideoInitialised === 'true') {
        return;
      }

      this.section.dataset.floatingVideoInitialised = 'true';

      this.observer = new IntersectionObserver(this.handleIntersection, {
        threshold: OBSERVER_THRESHOLD,
      });

      this.observer.observe(this.media);

      this.youtubeVideos.forEach((iframe) => {
        iframe.addEventListener('load', this.handleYouTubeLoad);
      });

      window.addEventListener('resize', this.handleResize);
    }

    get shopifyVideos() {
      return Array.from(
        this.section.querySelectorAll(SELECTORS.shopifyVideo)
      );
    }

    get youtubeVideos() {
      return Array.from(
        this.section.querySelectorAll(SELECTORS.youtubeVideo)
      );
    }

    get mediaItems() {
      return Array.from(
        this.section.querySelectorAll(SELECTORS.mediaItem)
      );
    }

    get activeMediaItem() {
      const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      const activeClass = isMobile
        ? CLASSES.mobileMedia
        : CLASSES.desktopMedia;

      return (
        this.mediaItems.find((item) => item.classList.contains(activeClass)) ||
        null
      );
    }

    get activeShopifyVideo() {
      return (
        this.activeMediaItem?.querySelector(SELECTORS.shopifyVideo) || null
      );
    }

    get activeYouTubeVideo() {
      return (
        this.activeMediaItem?.querySelector(SELECTORS.youtubeVideo) || null
      );
    }

    handleIntersection(entries) {
      const entry = entries[0];

      if (!entry) {
        return;
      }

      this.isIntersecting = entry.isIntersecting;

      if (!this.autoplay) {
        return;
      }

      if (this.isIntersecting) {
        this.playActiveVideo();
      } else {
        this.pauseAllVideos();
      }
    }

    handleResize() {
      this.pauseInactiveVideos();

      if (this.autoplay && this.isIntersecting) {
        this.playActiveVideo();
      }
    }

    handleYouTubeLoad(event) {
      const iframe = event.currentTarget;

      if (
        this.autoplay &&
        this.isIntersecting &&
        iframe === this.activeYouTubeVideo
      ) {
        this.playYouTubeVideo(iframe);
      }
    }

    playActiveVideo() {
      this.pauseInactiveVideos();

      const shopifyVideo = this.activeShopifyVideo;
      const youtubeVideo = this.activeYouTubeVideo;

      if (shopifyVideo) {
        const playPromise = shopifyVideo.play();

        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Autoplay can still be blocked by browser or device settings.
          });
        }

        return;
      }

      if (youtubeVideo) {
        this.playYouTubeVideo(youtubeVideo);
      }
    }

    pauseInactiveVideos() {
      const activeShopifyVideo = this.activeShopifyVideo;
      const activeYouTubeVideo = this.activeYouTubeVideo;

      this.shopifyVideos.forEach((video) => {
        if (video !== activeShopifyVideo) {
          video.pause();
        }
      });

      this.youtubeVideos.forEach((iframe) => {
        if (iframe !== activeYouTubeVideo) {
          this.pauseYouTubeVideo(iframe);
        }
      });
    }

    pauseAllVideos() {
      this.shopifyVideos.forEach((video) => {
        video.pause();
      });

      this.youtubeVideos.forEach((iframe) => {
        this.pauseYouTubeVideo(iframe);
      });
    }

    playYouTubeVideo(iframe) {
      this.sendYouTubeCommand(iframe, 'playVideo');
    }

    pauseYouTubeVideo(iframe) {
      this.sendYouTubeCommand(iframe, 'pauseVideo');
    }

    sendYouTubeCommand(iframe, command) {
      if (!iframe?.contentWindow) {
        return;
      }

      iframe.contentWindow.postMessage(
        JSON.stringify({
          event: 'command',
          func: command,
          args: [],
        }),
        'https://www.youtube.com'
      );
    }

    destroy() {
      this.pauseAllVideos();

      this.observer?.disconnect();

      this.youtubeVideos.forEach((iframe) => {
        iframe.removeEventListener('load', this.handleYouTubeLoad);
      });

      window.removeEventListener('resize', this.handleResize);

      delete this.section.dataset.floatingVideoInitialised;
    }
  }

  const instances = new Map();

  const initialiseSection = (section) => {
    if (!section || instances.has(section)) {
      return;
    }

    instances.set(section, new FloatingVideo(section));
  };

  const destroySection = (section) => {
    const instance = instances.get(section);

    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(section);
  };

  const initialiseAllSections = (container = document) => {
    if (container.matches?.(SELECTORS.section)) {
      initialiseSection(container);
    }

    container.querySelectorAll?.(SELECTORS.section).forEach(initialiseSection);
  };

  initialiseAllSections();

  document.addEventListener('shopify:section:load', (event) => {
    initialiseAllSections(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.(SELECTORS.section)
      ? event.target
      : event.target.querySelector?.(SELECTORS.section);

    destroySection(section);
  });
})();